import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCalendar,
  faCheckCircle,
  faClock,
  faDownload,
  faEuroSign,
  faExclamationTriangle,
  faEye,
  faFilter,
  faSearch,
  faStar,
  faTimesCircle,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

const CustomerHistoryPage: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');

  const breadcrumb = [{ label: 'Zákazníci' }, { label: 'Historie zákazníka' }];

  const customers = [
    { id: '1', name: 'Jana Nováková', email: 'jana.novakova@email.cz', phone: '+420 123 456 789' },
    { id: '2', name: 'Petr Svoboda', email: 'petr.svoboda@email.cz', phone: '+420 987 654 321' },
    { id: '3', name: 'Marie Krásná', email: 'marie.krasna@email.cz', phone: '+420 555 666 777' },
  ];

  const customerHistory = [
    {
      id: '1',
      date: '2024-11-15',
      time: '10:30',
      service: 'Střih a foukaná',
      staff: 'Anna Svobodová',
      duration: 60,
      price: 800,
      status: 'completed',
      notes: 'Spokojená zákaznice, objednala si další termín',
      rating: 5,
    },
    {
      id: '2',
      date: '2024-10-20',
      time: '14:00',
      service: 'Barvení',
      staff: 'Marie Krásná',
      duration: 120,
      price: 1500,
      status: 'completed',
      notes: 'Melír, velmi spokojená s výsledkem',
      rating: 5,
    },
    {
      id: '3',
      date: '2024-09-15',
      time: '11:00',
      service: 'Střih a foukaná',
      staff: 'Anna Svobodová',
      duration: 60,
      price: 800,
      status: 'completed',
      notes: 'Pravidelná klientka',
      rating: 4,
    },
    {
      id: '4',
      date: '2024-08-10',
      time: '16:00',
      service: 'Kosmetické ošetření',
      staff: 'Jana Nová',
      duration: 90,
      price: 1200,
      status: 'completed',
      notes: 'První kosmetické ošetření',
      rating: 5,
    },
    {
      id: '5',
      date: '2024-07-05',
      time: '13:30',
      service: 'Manikúra',
      staff: 'Jana Nová',
      duration: 60,
      price: 600,
      status: 'cancelled',
      notes: 'Zrušeno zákazníkem 1 hodinu před termínem',
      rating: null,
    },
  ];

  const filteredHistory = customerHistory.filter(
    visit =>
      visit.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.staff.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.notes.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedCustomerData = customers.find(c => c.id === selectedCustomer);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return faCheckCircle;
      case 'cancelled':
        return faTimesCircle;
      case 'no-show':
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no-show':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Dokončeno';
      case 'cancelled':
        return 'Zrušeno';
      case 'no-show':
        return 'Nedostavil se';
      default:
        return 'Neznámý';
    }
  };

  const renderStars = (rating: number | null) => {
    if (!rating) return <span className="text-gray-400">-</span>;
    return Array.from({ length: 5 }, (_, i) => <FontAwesomeIcon key={i} icon={faStar} className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} />);
  };

  const totalSpent = customerHistory.filter(v => v.status === 'completed').reduce((sum, v) => sum + v.price, 0);

  const averageRating =
    customerHistory.filter(v => v.rating !== null).reduce((sum, v) => sum + (v.rating || 0), 0) / customerHistory.filter(v => v.rating !== null).length;

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Historie zákazníka" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Customer Selection */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Výběr zákazníka</h2>
              <button className="flex items-center text-blue-600 hover:text-blue-800">
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
                Zpět na seznam
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <select
                value={selectedCustomer}
                onChange={e => setSelectedCustomer(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {/* Customer Info */}
          {selectedCustomerData && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faUser} className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{selectedCustomerData.name}</h3>
                  <p className="text-gray-600">{selectedCustomerData.email}</p>
                  <p className="text-gray-600">{selectedCustomerData.phone}</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Celkem utraceno</div>
                  <div className="text-2xl font-bold text-gray-900">{totalSpent.toLocaleString()} Kč</div>
                  <div className="text-sm text-gray-600">Průměrné hodnocení: {averageRating.toFixed(1)}/5</div>
                </div>
              </div>
            </section>
          )}

          {/* Stats Cards */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faCalendar} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem návštěv</p>
                  <p className="text-2xl font-bold text-gray-900">{customerHistory.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Dokončené</p>
                  <p className="text-2xl font-bold text-gray-900">{customerHistory.filter(v => v.status === 'completed').length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <FontAwesomeIcon icon={faStar} className="text-xl text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrné hodnocení</p>
                  <p className="text-2xl font-bold text-gray-900">{averageRating.toFixed(1)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem utraceno</p>
                  <p className="text-2xl font-bold text-gray-900">{totalSpent.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
          </section>

          {/* Search */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat v historii..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Filtry
              </button>
            </div>
          </section>

          {/* History Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Historie návštěv</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum a čas</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služba</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaměstnanec</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cena</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hodnocení</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHistory.map(visit => (
                    <tr key={visit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{new Date(visit.date).toLocaleDateString('cs-CZ')}</div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                          {visit.time}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{visit.service}</div>
                        <div className="text-sm text-gray-500">{visit.duration} min</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.staff}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{visit.price.toLocaleString()} Kč</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">{renderStars(visit.rating)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(visit.status)}`}>
                          <FontAwesomeIcon icon={getStatusIcon(visit.status)} className="w-3 h-3 mr-1" />
                          {getStatusText(visit.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit detail">
                            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                          </button>
                          <button className="text-gray-600 hover:text-gray-900 p-1" title="Stáhnout">
                            <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Notes Section */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Poznámky k zákazníkovi</h3>
            <div className="space-y-4">
              {customerHistory
                .filter(v => v.notes)
                .map(visit => (
                  <div key={visit.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(visit.date).toLocaleDateString('cs-CZ')} - {visit.service}
                      </span>
                      <span className="text-xs text-gray-500">{visit.staff}</span>
                    </div>
                    <p className="text-sm text-gray-600">{visit.notes}</p>
                  </div>
                ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CustomerHistoryPage;
